/* eslint-disable */
import type { RequestContext } from "../RequestContextProto.pb";
import type { ResponseContext } from "../ResponseContextProto.pb";
import type { CustomerVerificationData } from "../common/customer_verification.pb";
import type { DeviceLocationHeuristics } from "../common/device_location_heuristics.pb";
import type { SignInType } from "../common/sign_in_type.pb";
import type { ClientSecurityContext } from "./initiateSession.pb";

/** The request to register an email address as the first step to claiming it. */
export interface RegisterEmailRequest {
  request_context?: RequestContext;
  email_address?: string;
  /**
   * Deprecated. Use client scenario header.
   *
   * @deprecated
   */
  scenario?: RegisterEmailRequestScenario;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /** If true, this request will fail if a new customer must be created for this alias to be claimed. */
  existing_customer_only?: boolean;
  /**
   * Optional. If provided, this oauth enrollment token will be validated and used to customize the
   * challenge login code email that is sent. It is created during an OAuth enrollment flow.
   */
  enrollment_token?: string;
  device_location_heuristics?: DeviceLocationHeuristics;
  /** Tags are out of order. Next tag = 11. */
  account_id?: string;
}

export enum RegisterEmailRequestScenario {
  /** ONBOARDING - The customer is registering their email as part of onboarding. */
  ONBOARDING = "ONBOARDING",
  /** PROFILE - The customer is registering their email from their profile. */
  PROFILE = "PROFILE",
}

export interface RegisterEmailResponse {
  response_context?: ResponseContext;
  status?: RegisterEmailResponseStatus;
}

export enum RegisterEmailResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful, an email has been sent. */
  SUCCESS = "SUCCESS",
  /** INVALID_EMAIL_ADDRESS - The email address did not conform to an acceptable format. */
  INVALID_EMAIL_ADDRESS = "INVALID_EMAIL_ADDRESS",
  /**
   * TOO_MANY_REQUESTS - There have been too many requests to register this email address in the last 24 hours.
   * The user can try again later.
   */
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  /**
   * DUPLICATE_EMAIL_ADDRESS - The email address is already associated with the requesting user.
   * Added for 2.11
   */
  DUPLICATE_EMAIL_ADDRESS = "DUPLICATE_EMAIL_ADDRESS",
  /** CUSTOMER_DOES_NOT_EXIST - No customer exists for this email address, and existing_customer_only was set in the request. */
  CUSTOMER_DOES_NOT_EXIST = "CUSTOMER_DOES_NOT_EXIST",
  /** INVALID_ENROLLMENT_TOKEN - The enrollment_token did not exist, or was no longer valid. */
  INVALID_ENROLLMENT_TOKEN = "INVALID_ENROLLMENT_TOKEN",
}

/** The request to verify an email address with a verification code. */
export interface VerifyEmailRequest {
  request_context?: RequestContext;
  email_address?: string;
  verification_code?: string;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /**
   * The clients security / privacy context information. This data is currently used in client
   * app-level encryption and hardware attestation. Provide this when session is to be minted
   * during verifyEmail.
   */
  client_security_context?: ClientSecurityContext;
  /** Tags are out of order. Next tag = 10. */
  account_id?: string;
}

/** The response to a request to verify an email address. On success it contains the session token. */
export interface VerifyEmailResponse {
  response_context?: ResponseContext;
  status?: VerifyEmailResponseStatus;
  /** @deprecated */
  session_token?: string;
  /**
   * No longer used.
   *
   * @deprecated
   */
  customer_verification_data?: CustomerVerificationData;
  /** Tags are out of order. Next tag = 14 */
  sign_in_type?: SignInType;
}

export enum VerifyEmailResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful. A session token has been provided. */
  SUCCESS = "SUCCESS",
  /**
   * INVALID_EMAIL_ADDRESS - The email address provided did not conform to an acceptable format or has not been
   * previously registered.
   */
  INVALID_EMAIL_ADDRESS = "INVALID_EMAIL_ADDRESS",
  /** INVALID_VERIFICATION_CODE - The verification code is invalid. The user should retry with the correct verification code. */
  INVALID_VERIFICATION_CODE = "INVALID_VERIFICATION_CODE",
  /**
   * EXPIRED_VERIFICATION_CODE - The verification code has expired. The user should register their email address again to get
   *  a new verification code.
   */
  EXPIRED_VERIFICATION_CODE = "EXPIRED_VERIFICATION_CODE",
  /**
   * TOO_MANY_FAILED_ATTEMPTS - There have been too many failed attempts to verify this email address in the past 7 days.
   * The user can try again later.
   */
  TOO_MANY_FAILED_ATTEMPTS = "TOO_MANY_FAILED_ATTEMPTS",
  /** SUCCESS_WITH_VERIFICATION_DATA - No longer used. */
  SUCCESS_WITH_VERIFICATION_DATA = "SUCCESS_WITH_VERIFICATION_DATA",
  /** NOT_ELIGIBLE - Only for 2.5+ apps: this action was related to a customer merge that was deemed not eligible. */
  NOT_ELIGIBLE = "NOT_ELIGIBLE",
}
